import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import GoogleAutocomplete from "react-google-autocomplete";
import { setFilters } from "store/filters/operations";
import { getQueryParamsFromFilters } from "helpers/searchParams";
import styles from "./styles.module.scss";
import getCapacityOptions from "helpers/getCapacityOptions";
import { useTranslation } from "react-i18next";
import { FiltersDropdown } from "reusableComponents";
import { getUsageOptions } from "services/filters.services";
import { useLanguage } from "context";
import { LocationIcon, UsageIcon, CapacityIcon, SearchIcon } from "../../headerSvgIcons";

// Debounce utility to limit API calls
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

const HeaderSearchForm = () => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const [state, setState] = useState({
    place: { address: "", lat: null, lng: null },
    capacity: null,
    usage: null,
    isSubmitting: false
  });

  const [options, setOptions] = useState({
    capacity: [],
    usage: []
  });

  // Track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Update all options when language or translations change
  const updateAllOptions = useCallback(async () => {
    try {
      const usages = await getUsageOptions(language);
      const filteredUsages = usages.map(usage => ({
        value: usage.key,
        label: usage.name,
        ...usage
      }));

      const capacityOptions = getCapacityOptions({ usages: [] }, t);

      if (isMounted.current) {
        setOptions({
          usage: filteredUsages,
          capacity: capacityOptions
        });
      }
    } catch (error) {
      console.error("Error updating options:", error);
      const capacityOptions = getCapacityOptions({ usages: [] }, t);
      if (isMounted.current) {
        setOptions(prev => ({ ...prev, capacity: capacityOptions }));
      }
    }
  }, [language, t]);

  useEffect(() => {
    updateAllOptions();
  }, [updateAllOptions]);

  const handlePlaceSelect = (place) => {
    if (place && place.geometry && place.geometry.location) {
      setState(prev => ({
        ...prev,
        place: {
          address: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isSubmitting: true }));

    const filters = {
      text: state.place.address,
      lat: state.place.lat,
      lng: state.place.lng,
      capacity: state.capacity,
      usages: state.usage ? [state.usage] : []
    };

    try {
      const queryParams = getQueryParamsFromFilters(filters);
      await dispatch(setFilters(filters));
      navigate(`/search?${queryParams}`);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isMounted.current) {
        setState(prev => ({ ...prev, isSubmitting: false }));
      }
    }
  };

  // Don't render Google Autocomplete if API key is missing
  if (!googleMapsApiKey) {
    console.error("Google Maps API key is missing. Check your .env file");
    return (
      <form className={styles.searchForm} onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={`${styles.inputGroup} ${styles.searchInputContainer}`}>
            <div className={styles.inputIcon}>
              <LocationIcon />
            </div>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Maps API key missing"
              disabled
            />
          </div>
          {/* Rest of the form... */}
        </div>
      </form>
    );
  }

  return (
    <form className={styles.searchForm} onSubmit={handleSubmit}>
      <div className={styles.formRow}>
        {/* Location Input - with optimized autocomplete */}
        <div className={`${styles.inputGroup} ${styles.searchInputContainer}`}>
          <div className={styles.inputIcon}>
            <LocationIcon />
          </div>
          <GoogleAutocomplete
            apiKey={googleMapsApiKey}
            onPlaceSelected={handlePlaceSelect}
            options={{ 
              types: ["establishment", "geocode"],
              fields: ["formatted_address", "geometry", "name"],
              // CRITICAL OPTIMIZATIONS FOR COST:
              debounce: 300, // Wait 300ms after typing before making request
              minLength: 3, // Only search after 3 characters
              sessionToken: true // Use session tokens for better billing
            }}
            className={styles.searchInput}
            placeholder={t("search.bar.searchPlaceholder")}
            aria-label="Search for locations"
            // Additional props for cost control
            onInputChange={() => {}} // Empty to prevent unnecessary updates
            shouldFetchSuggestions={(input) => input.length >= 3} // Only fetch after 3 chars
          />
        </div>

        {/* Usage Dropdown */}
        <div className={styles.inputGroup}>
          <FiltersDropdown
            styled={false}
            options={options.usage}
            selected={state.usage}
            onSelect={(selected) => setState(prev => ({ ...prev, usage: selected }))}
            placeholder={t("search.defaultUsage")}
            aria-label="Select usage type"
            noOptionsMessage={() => t("search.noOptions")}
            forHeader
          />
        </div>

        {/* Capacity Dropdown */}
        <div className={styles.inputGroup}>
          <FiltersDropdown
            styled={false}
            options={options.capacity}
            selected={state.capacity}
            onSelect={(selected) => setState(prev => ({ ...prev, capacity: selected }))}
            placeholder={t("searchBar.persPlaceholder")}
            aria-label="Select capacity"
            forHeader
          />
        </div>

        {/* Search Button */}
        <button
          type="submit"
          className={styles.searchButton}
          disabled={state.isSubmitting}
          aria-label="Search"
        >
          {state.isSubmitting ? (
            <span className={styles.spinner} aria-hidden="true"></span>
          ) : (
            <SearchIcon />
          )}
        </button>
      </div>
    </form>
  );
};

export default HeaderSearchForm;


// import React, { useEffect, useState, useCallback } from "react";
// import { useDispatch } from "react-redux";
// import { useNavigate } from "react-router";
// import GoogleAutocomplete from "react-google-autocomplete";
// import { getGeocode, getLatLng } from "use-places-autocomplete";
// import { setFilters } from "store/filters/operations";
// import { getQueryParamsFromFilters } from "helpers/searchParams";
// import styles from "./styles.module.scss";
// import getCapacityOptions from "helpers/getCapacityOptions";
// import { useTranslation } from "react-i18next";
// import { FiltersDropdown } from "reusableComponents";
// import { getUsageOptions } from "services/filters.services";
// import { useLanguage } from "context";
// import { LocationIcon, UsageIcon, CapacityIcon, SearchIcon } from "../../headerSvgIcons";

// const HeaderSearchForm = () => {
//   const [t] = useTranslation("global");
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { language } = useLanguage();
//   const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

//   const [state, setState] = useState({
//     place: { address: "", lat: null, lng: null },
//     capacity: null,
//     usage: null,
//     isSubmitting: false
//   });

//   const [options, setOptions] = useState({
//     capacity: [],
//     usage: []
//   });

//   // Update all options when language or translations change
//   const updateAllOptions = useCallback(async () => {
//     try {
//       // Get usage options from API
//       const usages = await getUsageOptions(language);
//       const filteredUsages = usages
//         .map(usage => ({
//           value: usage.key,
//           label: usage.name,
//           ...usage
//         }));

//       // Get capacity options with current translations
//       const capacityOptions = getCapacityOptions({ usages: [] }, t);

//       setOptions({
//         usage: filteredUsages,
//         capacity: capacityOptions
//       });
//     } catch (error) {
//       console.error("Error updating options:", error);
//       // Fallback: at least update capacity options with current translations
//       const capacityOptions = getCapacityOptions({ usages: [] }, t);
//       setOptions(prev => ({ ...prev, capacity: capacityOptions }));
//     }
//   }, [language, t]);

//   useEffect(() => {
//     updateAllOptions();
//   }, [updateAllOptions]);

//   const handlePlaceSelect = async (place) => {
//     try {
//       const results = await getGeocode({ address: place.formatted_address });
//       const { lat, lng } = await getLatLng(results[0]);
//       setState(prev => ({
//         ...prev,
//         place: {
//           address: place.formatted_address,
//           lat,
//           lng
//         }
//       }));
//     } catch (error) {
//       console.error("Error fetching place details:", error);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     setState(prev => ({ ...prev, isSubmitting: true }));

//     const filters = {
//       text: state.place.address,
//       lat: state.place.lat,
//       lng: state.place.lng,
//       capacity: state.capacity,
//       usages: state.usage ? [state.usage] : []
//     };

//     try {
//       const queryParams = getQueryParamsFromFilters(filters);
//       await dispatch(setFilters(filters));
//       navigate(`/search?${queryParams}`);
//     } catch (error) {
//       console.error("Search error:", error);
//     } finally {
//       setState(prev => ({ ...prev, isSubmitting: false }));
//     }
//   };

//   const isFormValid = true;

//   return (
//     <form className={styles.searchForm} onSubmit={handleSubmit}>
//       <div className={styles.formRow}>
//         {/* Location Input */}
//         <div className={`${styles.inputGroup} ${styles.searchInputContainer}`}>
//           <div className={styles.inputIcon}>
//             <LocationIcon />
//           </div>
//           <GoogleAutocomplete
//             apiKey={googleMapsApiKey}
//             onPlaceSelected={handlePlaceSelect}
//             options={{ 
//               types: ["establishment", "geocode"],
//               fields: ["formatted_address", "geometry"]
//             }}
//             className={styles.searchInput}
//             placeholder={t("search.bar.searchPlaceholder")}
//             aria-label="Search for locations"
//           />
//         </div>

//         {/* Usage Dropdown */}
//         <div className={styles.inputGroup}>
//           <FiltersDropdown
//             styled={false}
//             options={options.usage}
//             selected={state.usage}
//             onSelect={(selected) => setState(prev => ({ ...prev, usage: selected }))}
//             placeholder={t("search.defaultUsage")}
//             aria-label="Select usage type"
//             noOptionsMessage={() => t("search.noOptions")}
//             forHeader
//           />
//         </div>

//         {/* Capacity Dropdown */}
//         <div className={styles.inputGroup}>
//           <div className={styles.inputIcon}>
//             <CapacityIcon />
//           </div>
//           <FiltersDropdown
//             styled={false}
//             options={options.capacity}
//             selected={state.capacity}
//             onSelect={(selected) => setState(prev => ({ ...prev, capacity: selected }))}
//             placeholder={t("searchBar.persPlaceholder")}
//             aria-label="Select capacity"
//             forHeader
//           />
//         </div>

//         {/* Search Button */}
//         <button
//           type="submit"
//           className={styles.searchButton}
//           disabled={!isFormValid || state.isSubmitting}
//           aria-label="Search"
//         >
//           {state.isSubmitting ? (
//             <span className={styles.spinner} aria-hidden="true"></span>
//           ) : (
//             <SearchIcon />
//           )}
//         </button>
//       </div>
//     </form>
//   );
// };

// export default HeaderSearchForm;